<template>
  <div class="huru-criminal-check-management-component">
    <!-- Header Section -->
    <div class="header-section mb-3">
      <h6 class="text-xs fw-semibold text-dark mb-1">
        <i class="fas fa-shield-alt me-2 text-primary"></i>South African
        Criminal Check Management
      </h6>
      <p class="text-xs text-muted mb-0">
        Manage and verify criminal records for this candidate application.
      </p>
    </div>

    <!-- No Data State -->
    <div
      v-if="!criminalCheck"
      class="card no-data-card border rounded-3 mx-auto text-center py-4 px-3"
    >
      <div
        class="card-body p-0 d-flex flex-column align-items-center justify-content-center"
      >
        <span class="no-data-icon mb-3 border rounded p-3">
          <i class="fas fa-fingerprint fa-2x text-primary"></i>
        </span>
        <span class="fw-bold text-dark text-xs mb-1"
          >No criminal check found</span
        >
        <div class="text-muted text-xs mb-3">
          No criminal check data available for this customer application.<br />
        </div>

        <!-- Inline Create Form -->
        <div v-if="showCreateForm" class="w-100 text-start mt-3">
          <div class="border rounded-3 p-3">
            <h6 class="text-xs fw-semibold text-dark mb-3">
              Subject Information
            </h6>
            <div class="row g-2">
              <div class="col-md-6">
                <label class="form-label text-xs text-muted mb-1"
                  >ID Number <span class="text-danger">*</span></label
                >
                <input
                  v-model="formData.identifier"
                  type="text"
                  class="form-control form-control-sm text-xs"
                  placeholder="Enter ID number"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label text-xs text-muted mb-1"
                  >ID Type</label
                >
                <select
                  v-model="formData.id_type"
                  class="form-select form-select-sm text-xs"
                >
                  <option value="idnumber">ID Number</option>
                  <option value="passport">Passport</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label text-xs text-muted mb-1"
                  >First Name</label
                >
                <input
                  v-model="formData.first_name"
                  type="text"
                  class="form-control form-control-sm text-xs"
                  placeholder="Enter first name"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label text-xs text-muted mb-1"
                  >Last Name</label
                >
                <input
                  v-model="formData.last_name"
                  type="text"
                  class="form-control form-control-sm text-xs"
                  placeholder="Enter last name"
                />
              </div>
            </div>
            <div class="d-flex gap-2 justify-content-end mt-3">
              <button
                type="button"
                @click="showCreateForm = false"
                class="btn btn-outline-secondary rounded-3 btn-sm text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                @click="createCriminalCheck"
                class="btn btn-bg-primary rounded-3 btn-sm text-xs"
              >
                <i class="fas fa-save me-1"></i>Create
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="service-section">
      <!-- Pending Status Section -->
      <div v-if="criminalCheck.status === 'pending'" class="form-section">
        <div class="form-section-header pb-3 border-bottom">
          <h6 class="text-xs fw-semibold text-dark mb-1">
            Subject Information
          </h6>
          <p class="text-xs text-muted mb-0">
            Review the subject details below before sending for verification.
          </p>
        </div>

        <!-- Subject Info Display -->
        <div class="subject-info mt-3">
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label text-xs text-muted mb-1"
                >ID Number</label
              >
              <input
                v-model="formData.identifier"
                type="text"
                class="form-control form-control-sm text-xs"
                placeholder="Enter ID number"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs text-muted mb-1">ID Type</label>
              <select
                v-model="formData.id_type"
                class="form-select form-select-sm text-xs"
              >
                <option value="idnumber">ID Number</option>
                <option value="passport">Passport</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs text-muted mb-1"
                >First Name</label
              >
              <input
                v-model="formData.first_name"
                type="text"
                class="form-control form-control-sm text-xs"
                placeholder="Enter first name"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs text-muted mb-1"
                >Last Name</label
              >
              <input
                v-model="formData.last_name"
                type="text"
                class="form-control form-control-sm text-xs"
                placeholder="Enter last name"
              />
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons mt-4 d-flex gap-2 justify-content-end">
          <button
            type="button"
            @click="resetForm"
            class="btn btn-outline-secondary rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-redo me-1"></i>Reset
          </button>
          <button
            type="button"
            @click="saveChanges"
            class="btn btn-bg-secondary rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-save me-1"></i>Save Changes
          </button>
          <button
            type="button"
            @click="sendForVerification"
            class="btn btn-bg-success rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-paper-plane me-1"></i>Send for Verification
          </button>
        </div>
      </div>

      <!-- Sent Status Section -->
      <div
        v-else-if="['sent', 'awaiting_profile'].includes(criminalCheck.status)"
        class="sent-section"
      >
        <div class="sent-status-header mb-3 pb-3 border-bottom">
          <h6 class="text-xs fw-semibold text-dark mb-1">
            <template v-if="!hasOrderNumber">
              <i class="fas fa-clock text-warning me-2"></i>Awaiting Profile
              Registration
            </template>
            <template v-else> Verification In Progress </template>
          </h6>
          <p class="text-xs text-muted mb-0">
            <template v-if="!hasOrderNumber">
              The identifier has not yet been registered. A notify alert has
              been set up. Click "Check Status" to see if the profile has been
              loaded.
            </template>
            <template v-else>
              Your criminal check is being processed. Click below to retrieve
              the verification results.
            </template>
          </p>
        </div>

        <!-- Subject Info Summary (visible while waiting) -->
        <div class="subject-summary mb-3">
          <div class="row g-2">
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">ID Number</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.identifier
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">ID Type</span>
                <span class="text-xs fw-semibold text-dark text-capitalize">{{
                  criminalCheck.id_type
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">First Name</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.first_name || "-"
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Last Name</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.last_name || "-"
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Order Number</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.order_number || "Awaiting Profile"
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Reason</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.reason
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!hasOrderNumber"
          class="card rounded-3 border-warning mb-2"
        >
          <div class="card-body text-center py-4">
            <i class="fa fa-hourglass-half fa-2x text-warning"></i>
            <h6 class="text-dark mb-1">Awaiting Profile Registration</h6>
            <p class="text-xs text-muted mb-0">
              The identifier is not yet registered in the system. A notify alert
              has been registered. Each status check will also try to create the
              order automatically once the profile becomes available.
            </p>
          </div>
        </div>

        <div v-else class="card rounded-3 border-info mb-2">
          <div class="card-body text-center py-4">
            <i class="fa fa-info-circle fa-2x text-info"></i>
            <h6 class="text-dark mb-1">Criminal Check Sent</h6>
            <p class="text-xs text-muted mb-0">
              The verification process may take some time. Continue checking
              until the report is available and the request is completed.
            </p>
          </div>
        </div>

        <div class="action-buttons d-flex gap-2 justify-content-end">
          <button
            @click="retrieveResults"
            class="btn btn-bg-info rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-sync-alt me-1"></i
            >{{
              hasOrderNumber ? "Retrieve Results" : "Check Status"
            }}
          </button>
        </div>
      </div>

      <!-- Completed Status Section -->
      <div
        v-else-if="criminalCheck.status === 'completed'"
        class="verified-section"
      >
        <div class="verified-status-header mb-3 pb-3 border-bottom">
          <h6 class="text-xs fw-semibold text-dark mb-1">
            <i class="fas fa-check-circle text-success me-2"></i>Results
            Retrieved
          </h6>
          <p class="text-xs text-muted mb-0">
            Criminal check results have been retrieved. The report below shows
            the verification result.
          </p>
        </div>

        <!-- Subject Info Summary -->
        <div class="subject-summary mb-3">
          <div class="row g-2">
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">ID Number</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.identifier
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Name</span>
                <span class="text-xs fw-semibold text-dark"
                  >{{ criminalCheck.first_name }}
                  {{ criminalCheck.last_name }}</span
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Order Number</span>
                <span class="text-xs fw-semibold text-dark">{{
                  criminalCheck.order_number
                }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Status</span>
                <span class="badge badge-success text-xs">Completed</span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="pdfDataUrl || criminalCheck.results || criminalCheck.document"
          class="alert alert-info py-2 px-3 mb-3 d-flex align-items-center"
          role="alert"
        >
          <i class="fas fa-lock me-2"></i>
          <span class="text-xs">
            The password to open the report is the data subject’s (or
            candidate’s) date of birth in the format <strong>CCYYMMDD</strong>;
            with no slashes, dashes, or spaces.
          </span>
        </div>

        <!-- Report Viewer: PDF + Export -->
        <ReportViewer
          v-if="pdfDataUrl || criminalCheck.results || criminalCheck.document"
          :response_doc="pdfDataUrl"
          title="Criminal Check PDF Report"
          service_type="huru_criminal_check"
          :export_data="criminalCheck.results || {}"
        >
          <template #export-actions>
            <button
              @click="resetAndResend"
              class="btn btn-outline-secondary rounded-3 btn-sm text-xs"
            >
              <i class="fas fa-redo me-1"></i>Reset and Resend
            </button>
          </template>
        </ReportViewer>
      </div>

      <!-- Failed Status Section -->
      <div v-else-if="criminalCheck.status === 'failed'" class="failed-section">
        <div class="card rounded-3 border-danger mb-2">
          <div class="card-body text-center py-4">
            <i class="fa fa-exclamation-triangle fa-2x text-danger"></i>
            <h6 class="text-dark mb-1">Criminal Check Failed</h6>
            <p class="text-xs text-muted mb-0">
              An error occurred while processing the criminal check. Please try
              again.
            </p>
          </div>
        </div>

        <div class="action-buttons d-flex gap-2 justify-content-end">
          <button
            @click="resetAndResend"
            class="btn btn-outline-primary rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-redo me-1"></i>Retry
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import ReportViewer from "@/components/v2_report/ReportViewer.vue";

export default {
  name: "HuruCriminalCheckManagement",
  components: { ReportViewer },
  props: {
    customerApplicationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      criminalCheck: null,
      showCreateForm: false,
      formData: {
        identifier: "",
        id_type: "idnumber",
        first_name: "",
        last_name: "",
        id_verification: false,
        reason: "Criminal check request",
      },
      originalFormData: {},
      base_url: API_BASE_URL + "/verify_api",
      isLoading: false,
      collapsed_sections: {
        search_results: false,
        order_profiles: false,
        result_items: false,
      },
    };
  },
  mounted() {
    this.loadCriminalCheck();
  },
  computed: {
    /**
     * The root results object stored on the criminal check record.
     */
    resultsRoot() {
      return this.criminalCheck?.results || {};
    },
    /**
     * Order-level summary present in the order-response shape.
     */
    orderSummary() {
      const r = this.resultsRoot;
      return r.order_number || r.order_status || r.status ? r : null;
    },
    /**
     * Flattened key-value pairs for display in the order summary card.
     */
    orderSummaryFields() {
      const r = this.resultsRoot;
      const fields = {};
      if (r.order_number) fields["Order Number"] = r.order_number;
      if (r.order_date)
        fields["Order Date"] = new Date(r.order_date).toLocaleString();
      if (r.order_status) fields["Order Status"] = r.order_status;
      if (r.order_method) fields["Order Method"] = r.order_method;
      if (r.transaction_id != null) fields["Transaction ID"] = r.transaction_id;
      if (r.huru_unique_ref) fields["Huru Ref"] = r.huru_unique_ref;
      if (r.meta_data?.request_reference)
        fields["Request Ref"] = r.meta_data.request_reference;
      if (r.meta_data?.timestamp)
        fields["Timestamp"] = new Date(r.meta_data.timestamp).toLocaleString();
      if (r.meta_data?.duration) fields["Duration (ms)"] = r.meta_data.duration;
      return fields;
    },
    /**
     * search_results array from the order-response shape.
     */
    searchResults() {
      return this.resultsRoot?.search_results || [];
    },
    /**
     * order_profiles array from the order-response shape.
     */
    orderProfiles() {
      return this.resultsRoot?.order_profiles || [];
    },
    /**
     * results array from the HuruCriminalCheckResultsResponse polling shape.
     */
    resultItems() {
      return this.resultsRoot?.results || [];
    },
    /**
     * Build a viewable URL for the PDF report.
     *
     * Priority:
     *  1. criminalCheck.document  – presigned S3 URL or Base64 returned by the API
     *  2. results.PdfReport       – Base64 embedded in the order response
     */
    pdfDataUrl() {
      const doc = this.criminalCheck?.document;
      if (doc && typeof doc === "string" && doc.trim() !== "") {
        // Pre-signed S3 URL or existing data URL – use directly
        if (doc.startsWith("http") || doc.startsWith("data:")) return doc;
        // Raw Base64 string
        return `data:application/pdf;base64,${doc}`;
      }
      // Fallback: PdfReport embedded inside the stored results dict
      const pdf = this.resultsRoot?.PdfReport;
      if (!pdf || typeof pdf !== "string" || pdf.trim() === "") return null;
      if (pdf.startsWith("data:")) return pdf;
      return `data:application/pdf;base64,${pdf}`;
    },
    hasOrderNumber() {
      return !!(this.criminalCheck?.order_number || "").trim();
    },
  },
  methods: {
    /** Toggle collapse state of a results sub-section. */
    toggle_section(key) {
      this.collapsed_sections[key] = !this.collapsed_sections[key];
    },
    /** Return Bootstrap badge class based on order status string. */
    orderStatusBadgeClass(status) {
      const map = {
        completed: "badge-success",
        cancelled: "badge-danger",
        pending: "badge-warning",
        active: "badge-info",
        failed: "badge-danger",
      };
      return map[(status || "").toLowerCase()] || "badge-secondary";
    },
    /** Badge class for consent field. */
    consentBadgeClass(consent) {
      if (!consent) return "bg-secondary";
      return consent.toLowerCase() === "restricted"
        ? "bg-warning text-dark"
        : "bg-info text-dark";
    },
    /** Badge class for outcome field. */
    outcomeBadgeClass(outcome) {
      if (!outcome) return "bg-secondary";
      const o = outcome.toLowerCase();
      if (o.includes("clear") || o.includes("pass")) return "badge-success";
      if (o.includes("fail") || o.includes("adverse")) return "badge-danger";
      return "bg-secondary";
    },
    async loadCriminalCheck() {
      this.isLoading = true;
      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({
          customer_application_id: this.customerApplicationId,
        });
        const response = await fetch(
          `${
            this.base_url
          }/background_screening/huru_criminal_check/get-huru-criminal-check-by-customer-application/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const data = await response.json();
        if (data.status === "success") {
          this.criminalCheck = data.data.huru_criminal_check;
          this.formData = {
            identifier: this.criminalCheck.identifier || "",
            id_type: this.criminalCheck.id_type || "idnumber",
            first_name: this.criminalCheck.first_name || "",
            last_name: this.criminalCheck.last_name || "",
            id_verification: this.criminalCheck.id_verification || false,
            reason: this.criminalCheck.reason || "Criminal check request",
          };
          this.originalFormData = { ...this.formData };
        } else {
          console.warn("No criminal check found:", data);
          this.criminalCheck = null;
        }
      } catch (error) {
        console.error("Error loading criminal check:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async createCriminalCheck() {
      if (!this.formData.identifier) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please enter an ID number.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return;
      }

      try {
        Swal.fire({
          title: "Creating...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const token = localStorage.getItem("token");
        const response = await fetch(
          `${this.base_url}/background_screening/huru_criminal_check/admin-create-huru-criminal-check/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              customer_application_id: this.customerApplicationId,
              ...this.formData,
            }),
          },
        );
        const data = await response.json();

        if (data.status === "success") {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Criminal check created successfully.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.showCreateForm = false;
          this.loadCriminalCheck();
        } else {
          throw new Error(data.message || "Failed to create criminal check.");
        }
      } catch (error) {
        console.error("Error creating criminal check:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to create criminal check.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async saveChanges() {
      if (!this.criminalCheck || !this.criminalCheck.id) {
        Swal.fire({
          icon: "warning",
          title: "No Record",
          text: "No criminal check record to update.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return;
      }

      try {
        Swal.fire({
          title: "Saving...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const token = localStorage.getItem("token");
        const response = await fetch(
          `${this.base_url}/background_screening/huru_criminal_check/admin-edit-huru-criminal-check/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              huru_criminal_check_id: this.criminalCheck.id,
              ...this.formData,
            }),
          },
        );
        const data = await response.json();

        if (data.status === "success") {
          Swal.fire({
            icon: "success",
            title: "Saved",
            text: "Changes saved successfully. Send for verification when ready.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.originalFormData = { ...this.formData };
          this.loadCriminalCheck();
        } else {
          throw new Error(data.message || "Failed to save changes.");
        }
      } catch (error) {
        console.error("Error saving changes:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to save changes.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async sendForVerification() {
      const confirmed = await Swal.fire({
        icon: "question",
        title: "Send for Verification?",
        text: "Are you sure you want to send this criminal check for verification?",
        showCancelButton: true,
        confirmButtonText: "Yes, Send",
        cancelButtonText: "Cancel",
      });

      if (!confirmed.isConfirmed) return;

      try {
        Swal.fire({
          title: "Sending...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const token = localStorage.getItem("token");
        const response = await fetch(
          `${this.base_url}/background_screening/huru_criminal_check/admin-send-huru-criminal-check/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              huru_criminal_check_id: this.criminalCheck.id,
            }),
          },
        );
        const data = await response.json();

        if (data.status === "success") {
          const huruStatus = data.data?.huru_status;

          if (huruStatus === "awaiting_profile") {
            // No profile yet — notifyme registered
            Swal.fire({
              icon: "info",
              title: "Awaiting Profile",
              text: "The identifier is not yet registered. A notify alert has been set up. You can check back later.",
              timer: 4000,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Criminal check has been sent for verification.",
              timer: 2500,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          }
          this.loadCriminalCheck();
        } else {
          throw new Error(data.message || "Failed to send for verification.");
        }
      } catch (error) {
        console.error("Error sending for verification:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to send for verification.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async retrieveResults() {
      try {
        Swal.fire({
          title: "Retrieving...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const token = localStorage.getItem("token");
        const response = await fetch(
          `${this.base_url}/background_screening/huru_criminal_check/admin-check-huru-criminal-status/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              huru_criminal_check_id: this.criminalCheck.id,
            }),
          },
        );
        const data = await response.json();

        if (data.status !== "success") {
          throw new Error(data.message || "Failed to retrieve results.");
        }

        const huruStatus = data.data?.huru_status;
        const returnedOrderNumber = data.data?.order_number || data.data?.response?.order_number || "";

        if (returnedOrderNumber && !this.criminalCheck.order_number) {
          this.criminalCheck = {
            ...this.criminalCheck,
            order_number: returnedOrderNumber,
            status: "sent",
          };
        }

        // ── Success: results are ready ──
        if (huruStatus === "success") {
          Swal.fire({
            icon: "success",
            title: "Results Retrieved",
            text:
              data.data.message ||
              "Criminal check results retrieved successfully.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.loadCriminalCheck();
          return;
        }

        // ── Awaiting Profile: not yet ──
        if (huruStatus === "awaiting_profile") {
          Swal.fire({
            icon: "info",
            title: "Profile Not Yet Loaded",
            text:
              data.data.message ||
              "The identifier has not yet been registered. Please try again later.",
            timer: 4000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.loadCriminalCheck();
          return;
        }

        // ── Pending: still processing ──
        if (huruStatus === "pending") {
          Swal.fire({
            icon: "info",
            title: "Results Not Ready",
            text:
              data.data.message ||
              "Results are not yet ready. Please poll again later.",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.loadCriminalCheck();
          return;
        }

        // ── Failed / Cancelled ──
        if (huruStatus === "failed") {
          Swal.fire({
            icon: "error",
            title: "Check Failed",
            text:
              data.data.message ||
              "One or more checks have failed or been cancelled.",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.loadCriminalCheck();
          return;
        }

        // ── Fallback: unexpected huru_status ──
        Swal.fire({
          icon: "info",
          title: "Awaiting Results",
          text: data.data?.message || "Awaiting results.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.message ||
            "Verification is still in progress. Please try again later.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async resetAndResend() {
      const confirmed = await Swal.fire({
        title: "Reset Criminal Check?",
        text: "This will reset the criminal check to pending status so it can be edited and re-sent. Are you sure?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Reset",
      });

      if (!confirmed.isConfirmed) return;

      try {
        Swal.fire({
          title: "Resetting...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const token = localStorage.getItem("token");
        const response = await fetch(
          `${this.base_url}/background_screening/huru_criminal_check/admin-reset-huru-criminal-check/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              huru_criminal_check_id: this.criminalCheck.id,
            }),
          },
        );
        const data = await response.json();

        if (data.status !== "success") {
          throw new Error(
            data.message ||
              data.data?.error ||
              "Failed to reset criminal check.",
          );
        }

        Swal.fire({
          icon: "success",
          title: "Reset",
          text: "Criminal check has been reset. You can now edit and re-send.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });

        await this.loadCriminalCheck();
      } catch (error) {
        console.error("Error resetting criminal check:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to reset criminal check.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    resetForm() {
      this.formData = { ...this.originalFormData };
      Swal.fire({
        icon: "info",
        title: "Reset",
        text: "Form has been reset to last saved values.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style scoped>
/* Component Wrapper */
.huru-criminal-check-management-component {
  padding: 0.75rem;
}

/* Header Section */
.header-section {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

/* Service Section Styles */
.service-section {
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem 0.75rem;
}

.service-section:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
}

/* Status Badge Section */
.badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  letter-spacing: 0.5px;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.badge-info {
  background-color: #cfe2ff;
  color: #084298;
  border: 1px solid #b6d4fe;
}

.badge-success {
  background-color: #d1e7dd;
  color: #0f5132;
  border: 1px solid #badbcc;
}

.badge-danger {
  background-color: #f8d7da;
  color: #842029;
  border: 1px solid #f5c2c7;
}

/* Form Section */
.form-section-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
}

/* Subject Summary */
.subject-summary .border {
  background-color: #f8f9fa;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-buttons .btn {
  padding: 0.375rem 1rem;
  font-weight: 500;
  font-size: 0.75rem;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.action-buttons .btn i {
  margin-right: 0.25rem;
}

.action-buttons .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Status Sections */
.sent-section,
.verified-section,
.form-section,
.failed-section {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.sent-status-header,
.verified-status-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
}

/* Results Container */
.results-container .table th,
.results-container .table td {
  font-size: 0.7rem;
  vertical-align: middle;
  white-space: nowrap;
}

/* No Data Card */
.no-data-card {
  background: #fff;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

.no-data-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .btn {
    width: 100%;
  }
}

@media (max-width: 576px) {
  .huru-criminal-check-management-component {
    padding: 0.5rem;
  }

  .service-section {
    padding: 0.75rem;
  }
}

@media print {
  .action-buttons {
    display: none;
  }

  .service-section {
    page-break-inside: avoid;
  }
}
</style>
